/**
 * Presets Store
 *
 * Manages both default presets (static files served from /presets/)
 * and user custom presets (fetched over gRPC).
 *
 * Default presets are loaded from /presets/manifest.json (no auth needed).
 *
 * Combined list: user custom presets first, then defaults.
 */

import { create } from 'zustand';
import toast from 'react-hot-toast';
import { listColorPresets } from '../services/visionAssetsClient.js';

// Cache the default presets manifest (same for all users, never changes at runtime)
let defaultPresetsCache = null;

async function loadDefaultPresets() {
  if (defaultPresetsCache) return defaultPresetsCache;
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}presets/manifest.json`);
    if (!res.ok) throw new Error(`Failed to load preset manifest: ${res.status}`);
    defaultPresetsCache = await res.json();
    return defaultPresetsCache;
  } catch (error) {
    console.error('[PresetsStore] Failed to load default presets:', error);
    return [];
  }
}

export const usePresetsStore = create((set, get) => ({
  presets: [],
  isLoaded: false,
  isLoading: false,

  /**
   * Load presets if not already cached.
   * Returns the cached presets immediately if available.
   */
  loadPresets: async () => {
    const state = get();
    if (state.isLoaded) return state.presets;
    if (state.isLoading) {
      // Resolve when the in-flight load finishes — success OR failure. Keying on isLoaded hangs
      // (and leaks this subscription) when the load rejects, since the failure path clears
      // isLoading without ever setting isLoaded.
      return new Promise((resolve) => {
        const unsub = usePresetsStore.subscribe((s) => {
          if (!s.isLoading) {
            unsub();
            resolve(s.presets);
          }
        });
      });
    }

    set({ isLoading: true });
    try {
      // Load defaults and user presets in parallel
      const [defaults, userPresets] = await Promise.all([
        loadDefaultPresets(),
        listColorPresets(),
      ]);

      // Merge: user presets first, then defaults
      const combined = [...(userPresets || []), ...(defaults || [])];
      set({ presets: combined, isLoaded: true, isLoading: false });
      return combined;
    } catch (error) {
      console.error('[PresetsStore] Load error:', error);
      toast.error('Could not load color presets');
      set({ isLoading: false });
      return [];
    }
  },

  /**
   * Add a new preset to the cache (after upload).
   * New user presets go to the front of the list.
   */
  addPreset: (preset) => {
    set((state) => ({
      presets: [preset, ...state.presets],
    }));
  },

  /**
   * Remove a preset from the cache.
   */
  removePreset: (presetId) => {
    set((state) => ({
      presets: state.presets.filter((p) => p.id !== presetId),
    }));
  },

  /**
   * Move a user preset to the front of the list (before other user presets).
   * Default presets stay at the end.
   */
  bumpPreset: (presetId) => {
    set((state) => {
      const preset = state.presets.find((p) => p.id === presetId);
      if (!preset || preset.isDefault) return state;
      const rest = state.presets.filter((p) => p.id !== presetId);
      return { presets: [preset, ...rest] };
    });
  },

  /**
   * Mark cache as stale so next loadPresets() re-fetches from server.
   */
  invalidate: () => {
    set({ isLoaded: false });
  },

  /**
   * Clear the cache (call on logout so next user gets their own presets).
   */
  reset: () => {
    set({ presets: [], isLoaded: false, isLoading: false });
  },
}));
